import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Typography, Alert, Spin } from 'antd';
import { CircleCheck, CircleAlert } from 'lucide-react';
import Icon from '../../components/Icon';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyOnBoardingToken, setOnBoardingPassword } from '../../auth/onBoarding.service';
import { brandingConfig } from '../../theme/brandingConfig';

const { Title, Text } = Typography;

const containerStyle = {
  height: '100dvh',
  minHeight: '100%',
  padding: 16,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
};

const bgStyle = {
  content: '""',
  display: 'block',
  position: 'absolute',
  zIndex: -1,
  inset: 0,
  backgroundImage: `url(${brandingConfig.assets.emailBackground})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 450,
  boxShadow: 'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
};

const SetupPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid setup link.');
      setVerifying(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const result = await verifyOnBoardingToken(token);
        if (result.valid) {
          setTokenValid(true);
        } else {
          setError('This setup link has expired or is invalid.');
        }
      } catch (err) {
        setError('Failed to verify link. Please try again.');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const validatePassword = (pwd) => {
    if (pwd.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordError(validatePassword(newPassword));

    if (confirmPassword && newPassword !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);

    if (password !== newConfirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    const passwordValidationError = validatePassword(password);

    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      await setOnBoardingPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to set up password.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  if (verifying) {
    return (
      <div style={containerStyle}>
        <div style={bgStyle} />
        <Card style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin size="large" style={{ marginBottom: 16 }} />
            <div><Text>Verifying Setup Link...</Text></div>
            <Text type="secondary">Please wait while we verify your setup link.</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (!tokenValid && !success) {
    return (
      <div style={containerStyle}>
        <div style={bgStyle} />
        <Card style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Icon as={CircleAlert} size={48} strokeWidth={2.5} style={{ color: '#E68A4C', marginBottom: 16 }} />
            <Title level={5} type="danger">Invalid Setup Link</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              {error || 'This setup link is invalid or has expired.'}
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div style={containerStyle}>
        <div style={bgStyle} />
        <Card style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Icon as={CircleCheck} size={48} strokeWidth={2.5} style={{ color: '#9FBDB1', marginBottom: 16 }} />
            <Title level={5} style={{ color: '#9FBDB1' }}>Password Setup Successful!</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              You can sign in and start onboarding now.
            </Text>
            <Button type="primary" onClick={handleGoToLogin} block>
              Sign In Now
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={bgStyle} />
      <Card style={cardStyle}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 12,
            marginBottom: 24,
          }}
        >
          <img
            src={brandingConfig.logos.horizontalBlack}
            alt="AirSmart Logo"
            style={{ width: 200, height: 'auto' }}
          />
          <span style={{ fontWeight: 'bold', fontSize: '1.25rem', lineHeight: 1.4 }}>
            Welcome to AirSmart Dealer Portal
          </span>
        </div>

        <Alert type="info" message="To set your password, please enter a new password and confirm it below." style={{ marginBottom: 16 }} />

        {error && (
          <Alert type="error" message={error} style={{ marginBottom: 16 }} />
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <Text>New Password</Text>
            <Input.Password
              placeholder="New Password"
              value={password}
              onChange={handlePasswordChange}
              status={passwordError ? 'error' : ''}
              autoFocus
              style={{ marginTop: 4 }}
            />
            <Text type={passwordError ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
              {passwordError || 'Minimum 6 characters required'}
            </Text>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text>Confirm New Password</Text>
            <Input.Password
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              status={confirmPasswordError ? 'error' : ''}
              style={{ marginTop: 4 }}
            />
            <Text type={confirmPasswordError ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
              {confirmPasswordError || 'Re-enter your new password'}
            </Text>
          </div>

          <Button
            type="primary"
            htmlType="submit"
            block
            loading={loading}
          >
            {loading ? 'Setting Password...' : 'Set Password'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default SetupPasswordPage;
